/* Product detail motion is finite and optional. Native product purchasing stays independent. */
(() => {
  'use strict';
  const key = Symbol.for('xarcade.pinball.product.motion');
  if (window[key]) { window[key].boot(); return; }
  const instances = new Map();
  const rootSelector = '[data-pinball-native]';
  const revealSelector = '[data-pinball-reveal]';
  const purchaseSelector = '[data-pinball-product-form], [data-pinball-submit], .zx-buybox';

  function init(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const panels = [...root.querySelectorAll(revealSelector)];
    const motion = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    let observer = null;
    const impacts = new Map();
    const on = (target, type, handler) => target?.addEventListener(type, handler, { signal: abort.signal });
    const reveal = panel => {
      panel.classList.add('is-revealed');
      if (!impacts.has(panel)) observer?.unobserve(panel);
    };
    const revealAll = () => {
      delete root.dataset.pinballMotionReady;
      observer?.disconnect(); observer = null;
      panels.forEach(panel => panel.classList.add('is-revealed'));
      impacts.forEach(state => { state.visible = false; stopImpact(state); });
    };
    const dispose = () => {
      abort.abort(); revealAll(); instances.delete(root);
    };
    instances.set(root, { dispose });

    // A conceptual impact sequence runs once, then waits for an explicit replay.
    function statusText(state, message) {
      if (state.status && state.status.textContent !== message) state.status.textContent = message;
    }
    function stopImpact(state, run = state.run) {
      if (run !== state.run || !state.active) return;
      state.active = false; state.animation = null;
      state.panel.classList.remove('is-impacting');
      state.button?.setAttribute('aria-busy', 'false');
      state.button?.setAttribute('aria-disabled', 'false');
      if (state.manual) statusText(state, 'Conceptual impact illustration. Replay to view it again.');
    }
    function playImpact(state, manual = false) {
      if (state.active) return;
      if (!state.sentinel || !state.visible || !motion || motion.matches || document.hidden) {
        if (manual) statusText(state, motion?.matches ? 'Static impact illustration. Reduced motion is enabled.' : 'Conceptual impact illustration. Replay when the panel is visible.');
        return;
      }
      const run = ++state.run;
      state.active = true; state.manual = manual;
      state.panel.classList.add('is-impacting');
      state.button?.setAttribute('aria-busy', 'true');
      state.button?.setAttribute('aria-disabled', 'true');
      if (manual) statusText(state, 'Playing a visual impact illustration.');
      // Inspect only this small SVG sentinel at activation; never sample layout or run a frame loop.
      if (typeof state.sentinel.getAnimations === 'function') {
        const animations = state.sentinel.getAnimations();
        state.animation = animations.find(animation => animation.playState !== 'finished' && animation.playState !== 'idle') || null;
        if (!state.animation) {
          stopImpact(state, run);
          if (manual) statusText(state, 'Conceptual impact illustration. Animation is unavailable in this view.');
          return;
        }
        state.animation.finished.then(() => stopImpact(state, run), () => stopImpact(state, run));
      }
    }
    root.querySelectorAll('[data-pinball-impact]').forEach(panel => {
      const button = panel.querySelector('[data-pinball-impact-play]');
      const state = { panel, button, sentinel: panel.querySelector('[data-pinball-impact-sentinel]'), status: panel.querySelector('[data-pinball-impact-status]'), visible: false, active: false, played: false, manual: false, animation: null, run: 0 };
      impacts.set(panel, state);
      if (state.status && !state.status.hasAttribute('aria-live')) state.status.setAttribute('aria-live', 'polite');
      if (button) { button.type = 'button'; on(button, 'click', () => playImpact(state, true)); }
      on(state.sentinel, 'animationend', event => {
        if (event.target === state.sentinel && !state.animation) stopImpact(state);
      });
      on(state.sentinel, 'animationcancel', event => {
        if (event.target === state.sentinel && !state.animation) stopImpact(state);
      });
    });
    on(document, 'visibilitychange', () => { if (document.hidden) impacts.forEach(state => stopImpact(state)); });

    // Purchase controls are immediately visible, including accidentally tagged ancestors.
    panels.filter(panel => panel.matches(purchaseSelector) || panel.closest(purchaseSelector) || panel.querySelector(purchaseSelector)).forEach(reveal);
    const pending = panels.filter(panel => !panel.classList.contains('is-revealed'));
    if ((pending.length || impacts.size) && motion && !motion.matches && typeof window.IntersectionObserver === 'function') {
      try {
        observer = new window.IntersectionObserver(entries => {
          entries.forEach(entry => {
            const impact = impacts.get(entry.target);
            if (impact) { impact.visible = entry.isIntersecting; if (!impact.visible) stopImpact(impact); }
            if (entry.isIntersecting && entry.target.matches(revealSelector)) reveal(entry.target);
          });
          entries.forEach(entry => {
            const impact = impacts.get(entry.target);
            if (impact?.visible && !impact.played) { impact.played = true; playImpact(impact); }
          });
        }, { threshold: .08, rootMargin: '0px 0px -32px 0px' });
        new Set([...pending, ...impacts.keys()]).forEach(panel => observer.observe(panel));
        // CSS may enhance below-fold panels only after the observer is successfully attached.
        if (pending.length) root.dataset.pinballMotionReady = 'true';
      } catch (_) { revealAll(); }
    } else revealAll();
    on(motion, 'change', event => { if (event.matches) revealAll(); });
    // Keyboard navigation never leaves focused content visually concealed.
    on(root, 'focusin', event => {
      const panel = event.target.closest?.(revealSelector);
      if (panel && root.contains(panel)) reveal(panel);
    });

    root.querySelectorAll('[data-pinball-stage-demo]').forEach(demo => {
      const buttons = [...demo.querySelectorAll('button[data-stage-select]')].filter(button => ['1', '2'].includes(button.dataset.stageSelect));
      const status = demo.querySelector('[data-stage-status]');
      if (status && !status.hasAttribute('aria-live')) status.setAttribute('aria-live', 'polite');
      const select = stage => {
        demo.dataset.stage = stage;
        buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.stageSelect === stage)));
        const button = buttons.find(item => item.dataset.stageSelect === stage);
        const message = button?.dataset.stageStatusText || (stage === '2' ? 'Stage 2 illustration: lower and upper flippers engaged.' : 'Stage 1 illustration: lower flipper engaged.');
        if (status && status.textContent !== message) status.textContent = message;
      };
      buttons.forEach(button => {
        button.type = 'button';
        on(button, 'click', () => select(button.dataset.stageSelect));
      });
      select(demo.dataset.stage === '2' ? '2' : '1');
    });
  }

  function boot(scope = document) {
    if (scope.matches?.(rootSelector)) init(scope);
    scope.querySelectorAll(rootSelector).forEach(init);
  }
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => { if (event.target === root || event.target.contains(root)) instance.dispose(); });
  });
  window[key] = { boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
})();
